import { useEffect, useRef, useState } from "react";
import { WorkStatus } from "tm-shared/types";

import { MERCURE_HUB_URL } from "@/config";
import { addModalToQueue } from "@/store/slices/ui";
import { decreaseExportIndicator } from "@/store/slices/work";
import { ModalType } from "@/types/modals";

const useMercure = (id, workStatus, dispatch) => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const eventSourceRef = useRef<EventSource | null>(null);

    const connect = () => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const url = new URL(MERCURE_HUB_URL);
        url.searchParams.append("topic", id);

        const eventSource = new EventSource(url.toString());
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
            try {
                const parsed = JSON.parse(event.data);

                switch (parsed?.subject) {
                    case "trigger.promo.process": {
                        const callbackObject = JSON.parse(parsed.content);

                        if (callbackObject?.content?.consumedId) {
                            dispatch(
                                addModalToQueue({
                                    type: ModalType.Promos,
                                    id: callbackObject?.content?.consumedId,
                                    payload: {
                                        deliveryId: parsed?.deliveryId,
                                        promoInfo: {
                                            ...callbackObject?.content,
                                        },
                                    },
                                }),
                            );
                        } else {
                            dispatch(
                                addModalToQueue({
                                    id: callbackObject?.consumedId,
                                    type: ModalType.Promos,
                                    payload: {
                                        deliveryId: parsed?.deliveryId,
                                        promoInfo: {
                                            ...callbackObject,
                                        },
                                    },
                                }),
                            );
                        }
                        break;
                    }
                    case "trigger.getState": {
                        dispatch(
                            addModalToQueue({
                                id:
                                    parsed?.deliveryId ??
                                    event.timeStamp.toString(),
                                type: ModalType.State,
                                payload: {
                                    deliveryId: parsed?.deliveryId,
                                    ...parsed,
                                },
                            }),
                        );
                        break;
                    }
                    case "export.completed": {
                        const callbackObject = JSON.parse(parsed.content);

                        dispatch(
                            addModalToQueue({
                                id:
                                    parsed?.deliveryId ??
                                    event.timeStamp.toString(),
                                type: ModalType.Exports,
                                payload: {
                                    deliveryId: parsed?.deliveryId,
                                    ...callbackObject,
                                },
                            }),
                        );
                        dispatch(decreaseExportIndicator());
                        break;
                    }
                    case "trigger.kpi.progress": {
                        const callbackObject = JSON.parse(parsed.content);

                        dispatch(
                            addModalToQueue({
                                id:
                                    parsed?.deliveryId ??
                                    event.timeStamp.toString(),
                                type: ModalType.KpiProgress,
                                payload: {
                                    deliveryId: parsed?.deliveryId,
                                    ...callbackObject,
                                },
                            }),
                        );
                        break;
                    }
                    case "rpc.callback": {
                        const callbackObject = JSON.parse(parsed.content);

                        switch (callbackObject?.method) {
                            case "scheduler.addUnscheduledVisits": {
                                dispatch(
                                    addModalToQueue({
                                        id:
                                            parsed?.deliveryId ??
                                            event.timeStamp.toString(),
                                        type: ModalType.UnscheduledMarketCompletion,
                                        payload: {
                                            deliveryId: parsed?.deliveryId,
                                            marketsCount:
                                                callbackObject?.response?.result
                                                    ?.countUnscheduledMarkets,
                                        },
                                    }),
                                );
                                break;
                            }

                            default: {
                                dispatch(
                                    addModalToQueue({
                                        id:
                                            parsed?.deliveryId ??
                                            event.timeStamp.toString(),
                                        type: ModalType.Messages,
                                        payload: {
                                            deliveryId: parsed?.deliveryId,
                                            ...parsed,
                                        },
                                    }),
                                );
                                break;
                            }
                        }
                        break;
                    }
                    default: {
                        dispatch(
                            addModalToQueue({
                                id:
                                    parsed?.deliveryId ??
                                    event.timeStamp.toString(),
                                type: ModalType.Messages,
                                payload: {
                                    deliveryId: parsed?.deliveryId,
                                    ...parsed,
                                },
                            }),
                        );
                        break;
                    }
                }
            } catch (error) {
                console.error("Помилка парсинга повідомлення:", error);
            }
        };

        eventSource.onerror = () => {
            console.error("Помилка підключення до Mercure. З'єднання закрито.");
            eventSource.close();
        };
    };

    useEffect(() => {
        let swHandler: () => void;
        if ("serviceWorker" in navigator) {
            swHandler = () => setUpdateAvailable(true);
            navigator.serviceWorker.addEventListener(
                "controllerchange",
                swHandler,
            );
        }
        if (id) {
            connect();
        }

        const handleReconnect = () => {
            if (document.visibilityState !== "visible") return;

            if (id && workStatus === WorkStatus.StartWork) {
                console.log("Додаток активовано! Перепідключаємо Mercure...");
                connect();
            }
        };

        document.addEventListener("visibilitychange", handleReconnect);

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
            if ("serviceWorker" in navigator && swHandler) {
                navigator.serviceWorker.removeEventListener(
                    "controllerchange",
                    swHandler,
                );
            }
            document.removeEventListener("visibilitychange", handleReconnect);
        };
    }, [id, workStatus, dispatch]);

    return updateAvailable;
};

export default useMercure;
